// MATERIAL_FOLLOWUP: the daily Red and Yellow follow-ups (ICT-1, ICT-8).
// When the ICT Follow Ups report lands in the inbox (Alteryx, or the ICT-7
// manual pull), Ace works every row on it, so nobody has to ask for each one:
//   - plain chases are grouped into one email per solicitor, with every job
//     in that solicitor's section in the template grid (ICT-1);
//   - everything else (EDGAR/SEDAR search, POD, email only, abort, holds,
//     Broadridge RICS checks, issuer and VIF/BRE jobs) is its own task.
// A person can still ask for a follow-up on a job that isn't on the report
// (the Reports page's "Create follow-up").
//
// The decisioning is pure, tested logic this module wires to the registry:
// utils/proxyDecisioning.ts's decide() picks the cutoff action, and
// data/proxyTemplates.ts picks and renders the template.
import type { ProxyJob } from '../mockProxyJobs';
import { PROXY_SEED_TODAY } from '../mockProxyJobs';
import { ACTION_LABEL, bandFor, daysToMeeting, decide, ricsAction, RICS_STATUSES, type ProxyAction } from '../../utils/proxyDecisioning';
import { selectTemplate, renderTemplate, renderGridTemplate } from '../proxyTemplates';
import { classifyEmail } from '../../utils/proxyEmailClassifier';
import { pullFollowUpReport } from '../../utils/followUpReportPull';
import { resolveOutreachContact, contactNote } from '../proxyFallbackSolicitors';
import { calculateRdq } from '../../utils/rdqCalculator';
import type { ProxyInboxEmail } from '../mockProxyEmails';
import type {
  ProxyTask, ProxyWorkflowContext, ProxyWorkflowDefinition,
  ProxyExtractedField, ProxyCalcRow, ProxyReviewPanelSchema, ProxyPanelContext,
} from '../proxyWorkflowTypes';

// Actions where the cutoff rules engine actually calls for reaching out to
// the solicitor — ABORT/HOLD_MAIL_DATE/NO_ACTION mean exactly that: don't.
// Those still get documented (the MIC follow-up always fires), just without
// an outbound email.
const EMAILABLE_ACTIONS = new Set<ProxyAction>(['FOLLOW_UP', 'POD', 'EMAIL_ONLY_CANCEL_FULLSETS']);

// PULL_FILING (Red, 18-11 days to vote cutoff — ICT-8) doesn't chase anyone:
// Ace looks the filing up by CUSIP on the public portal and hands the PDF to
// verifications@ to clear the job and release emails only. Full sets can
// still arrive, so the job stays open. Demo returns a seeded result: the
// job's own pageCount.
const VERIFICATIONS_MAILBOX = 'verifications@betanxt.com';
function filingSource(job: ProxyJob): { portal: string; docType: string } {
  return job.country === 'CA'
    ? { portal: 'SEDAR+', docType: 'Management Information Circular' }
    : { portal: 'SEC EDGAR', docType: 'DEF 14A' };
}

const DEFAULT_CC = ['proxymaterial@betanxt.com'];
// ICT-1: a Mediant Communications follow-up also copies brokersearch@ and
// whichever team the MIC ops note names. CC only; the template is unchanged.
const MEDIANT_TEAM_CC: Record<string, string> = {
  'Green Team': 'mediant.green.team@betanxt.com',
  'Blue Team': 'mediant.blue.team@betanxt.com',
  'Gold Team': 'mediant.gold.team@betanxt.com',
  'ISG-BNY': 'adrproxy@betanxt.com',
};
function mediantCc(job: ProxyJob): string[] {
  const teams = Object.entries(MEDIANT_TEAM_CC).filter(([team]) => job.opsNotes.some(n => n.includes(team))).map(([, email]) => email);
  return [...DEFAULT_CC, 'brokersearch@betanxt.com', ...teams];
}

// Resolved once documented at or after the latest request, so the Follow Ups
// tracker can ask for the next chase on a job that was followed up before.
export function materialFollowupResolved(job: ProxyJob): boolean {
  const requested = job.materialFollowupRequestedAt || '';
  return job.followUpHistory.some(r => (r.type === 'material_followup_documented' || r.type === 'vif_bre_only') && r.timestamp >= requested);
}

// Worked today: the MIC follow-up has been logged on or after the request.
function documentedSince(job: ProxyJob, since: string): boolean {
  return job.followUpHistory.some(r => (r.type === 'material_followup_documented' || r.type === 'vif_bre_only') && r.timestamp >= since);
}

// One job's task. From the report email when Ace picked it up there,
// otherwise from a person's "Create follow-up".
export function buildMaterialFollowupTask(job: ProxyJob, requestedAt?: string, report?: ProxyInboxEmail): ProxyTask {
  const band = bandFor(daysToMeeting(job));
  const stamp = report?.date || requestedAt || PROXY_SEED_TODAY;
  return {
    id: `material-followup-${job.jobId}`,
    workflowType: 'MATERIAL_FOLLOWUP',
    jobId: job.jobId,
    title: `${job.issuer}: material follow-up`,
    subtitle: `Job ${job.jobId}`,
    reason: report
      ? `On today's ${band} follow-up report. Ace applied the ${band === 'Red' ? 'ICT-8' : 'ICT-1'} rules to decide what to do.`
      : `Requested from the ${band} report. Ace applied the cutoff rules to decide what to do.`,
    source: report ? 'email' : 'job_signal',
    sourceEmailId: report?.id,
    sourceLabel: report ? undefined : 'outstanding-material report',
    sourceDetail: report ? undefined : {
      rows: [
        { label: 'Report', value: `Outstanding Material, ${band}` },
        { label: 'Band', value: `${band} · ${daysToMeeting(job)}d to meeting · ${job.daysToVoteCutoff}d to vote cutoff` },
        { label: 'Requested', value: stamp },
      ],
    },
    createdAt: stamp,
    // Either a listed report row or a person's explicit request.
    confidence: 95,
  };
}

// A plain chase to a solicitor on the contact list can share an email with
// the rest of that solicitor's section. Anything with its own rule, check or
// exception stays a separate task.
function groupable(job: ProxyJob): boolean {
  const contact = resolveOutreachContact(job);
  return decisionFor(job).action === 'FOLLOW_UP' && contact?.source === 'contact_list'
    && !job.isBroadridge && !job.vifBreOnly && job.materialStatus !== 'Shortage';
}

function gridTemplate(job: ProxyJob) {
  return bandFor(daysToMeeting(job)) === 'Red' ? 'FU-Red' : job.country === 'CA' ? 'FU-Canadian' : 'FU-American';
}

function buildGroupTask(jobs: ProxyJob[], report: ProxyInboxEmail): ProxyTask {
  const band = bandFor(daysToMeeting(jobs[0]));
  const solicitor = jobs[0].solicitor!;
  return {
    id: `material-followup-${band.toLowerCase()}-${gridTemplate(jobs[0]).toLowerCase()}-${solicitor.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    workflowType: 'MATERIAL_FOLLOWUP',
    jobId: jobs[0].jobId,
    jobIds: jobs.map(j => j.jobId),
    title: `${solicitor}: ${jobs.length} ${band} follow-ups`,
    subtitle: `Jobs ${jobs.map(j => j.jobId).join(', ')}`,
    reason: `${solicitor} has ${jobs.length} jobs on today's ${band} report. ICT-1 sends them one email with every job in the grid.`,
    source: 'email',
    sourceEmailId: report.id,
    createdAt: report.date,
    confidence: 95,
  };
}

// A reply or bounce on this job that hasn't been dealt with, or was dealt
// with today. Either way the answer comes before another chase.
function awaitingResponse(job: ProxyJob, emails: ProxyInboxEmail[], today: string): boolean {
  if (job.followUpHistory.some(r => (r.type === 'reply_actioned' || r.type === 'email_bounce_verify') && r.timestamp.startsWith(today))) return true;
  return emails.some(e => {
    if (e.relatedJobId !== job.jobId) return false;
    const category = classifyEmail(e).category;
    const handled = (type: string) => job.followUpHistory.some(r => r.type === type && r.sourceEmailId === e.id);
    if (category === 'FOLLOW_UP_REPLY') return !handled('reply_actioned');
    if (category === 'BOUNCE') return !handled('email_bounce_verify');
    return false;
  });
}

function buildTasks({ jobs, emails, receipts }: ProxyWorkflowContext): ProxyTask[] {
  const report = emails.find(e => classifyEmail(e).category === 'FOLLOW_UP_REPORT');
  const tasks: ProxyTask[] = [];
  const covered = new Set<string>();
  if (report) {
    const rows = pullFollowUpReport(jobs, receipts, report.date).rows
      .map(r => jobs.find(j => j.jobId === r.job.jobId)!)
      // Skip what's done today, held, a shortage (ICT-13 chases those), a
      // contest (ICT-3 does the outreach), waiting
      // on an answer, or waiting on a contact (that's an Exception).
      .filter(job => !documentedSince(job, report.date)
        && job.materialStatus !== 'Shortage' && !job.contest
        && !awaitingResponse(job, emails, report.date)
        && !['NO_ACTION', 'HOLD_DISCOVERY'].includes(decisionFor(job).action)
        && (resolveOutreachContact(job) || !EMAILABLE_ACTIONS.has(decisionFor(job).action) || job.vifBreOnly));
    const groups = new Map<string, ProxyJob[]>();
    for (const job of rows) {
      if (!groupable(job)) continue;
      const key = `${bandFor(daysToMeeting(job))}|${gridTemplate(job)}|${job.solicitor}`;
      groups.set(key, [...(groups.get(key) || []), job]);
    }
    groups.forEach(group => {
      if (group.length < 2) return;
      tasks.push(buildGroupTask(group, report));
      group.forEach(j => covered.add(j.jobId));
    });
    for (const job of rows) {
      if (covered.has(job.jobId)) continue;
      tasks.push(buildMaterialFollowupTask(job, undefined, report));
      covered.add(job.jobId);
    }
  }
  // A person's request for a job that isn't on the report.
  for (const job of jobs) {
    if (covered.has(job.jobId) || !job.materialFollowupRequestedAt || materialFollowupResolved(job)) continue;
    tasks.push(buildMaterialFollowupTask(job, job.materialFollowupRequestedAt));
  }
  return tasks;
}

// What Ace read that a person can correct: whether the EDGAR/SEDAR search
// found the filing, and what RICS shows for a Broadridge job.
function defaultEdits(_task: ProxyTask, job: ProxyJob): Record<string, any> {
  return { filingFound: job.filingOnPortal, ricsStatus: job.ricsStatus ?? 'Awaiting Material', proxyCardOnFile: !!job.proxyCardPdfOnFile };
}

// ICT-1 exception comments, added above the template's sign-off.
function addComment(body: string, comment: string): string {
  return body.replace('\n\nThank you,', `\n\n${comment}\n\nThank you,`);
}

// ICT-1: with no RDQ sent, MIC's New Follow Up button is greyed out — a New
// Material line goes in first, quantity from the RDQ calculator.
function newMaterialStep(job: ProxyJob): string {
  const material = job.country === 'CA' ? 'Notice/Circular' : 'Proxy Statement';
  const qty = calculateRdq({
    country: job.country, noElec: false, mailingAsNaa: job.country === 'CA',
    activeFullSets: job.fullSetsNeeded, activeNaa: job.naaNeeded, activeElectronic: 0,
  }).lines[0].qtyToOrder;
  return `In MIC, add New Material first (no RDQ was sent, so New Follow Up is greyed out): ${material}, supplier Mediant Order, quantity ${qty} from the RDQ calculator`;
}

// Shown wherever the ICT-8 filing search applies, so a person can flip
// Ace's search result — "Not found" turns the task into a Red follow-up.
function filingField(job: ProxyJob, edits: Record<string, any>): ProxyExtractedField | null {
  if (decisionFor(job, { filingFound: true }).action !== 'PULL_FILING') return null;
  const found: boolean = edits.filingFound ?? job.filingOnPortal;
  return {
    key: 'filingFound', label: `${filingSource(job).portal} filing`, display: found ? 'Found' : 'Not found',
    editable: { kind: 'enum', value: found, options: [{ value: true, label: 'Found' }, { value: false, label: 'Not found' }] },
  };
}

function composeBody(decision: ReturnType<typeof decide>, templateBody: string): string {
  if (decision.action === 'EMAIL_ONLY_CANCEL_FULLSETS') {
    return `${templateBody}\n\nGiven the shortened timeline, please proceed via email only. Cancel any outstanding full set order, and do not print or ship full sets.`;
  }
  // POD limits are internal guidelines — externally the party is always asked
  // to send the material, or to pay for POD. No-cost POD is an internal call.
  if (decision.action === 'POD') {
    return `${templateBody}\n\nWe are at the 10-day mark for this job. Please send the outstanding material today. If you would like us to print the material on demand, please confirm approval of the printing charges.`;
  }
  return templateBody;
}

function decisionFor(job: ProxyJob, edits: Record<string, any> = {}) {
  return decide({
    daysToVoteCutoff: job.daysToVoteCutoff,
    daysToMeeting: daysToMeeting(job),
    filingPulled: job.followUpHistory.some(r => r.type === 'filing_pulled'),
    filingOnPortal: edits.filingFound ?? job.filingOnPortal,
    materialStatus: job.materialStatus,
    pageCount: job.pageCount,
    fullSetHolders: job.fullSetHolders,
    country: job.country,
    isBroadridge: job.isBroadridge,
    mailDate: job.mailDate,
    econsentOnly: job.econsentOnly,
    solicitor: job.solicitor,
    today: PROXY_SEED_TODAY,
    heldForDiscovery: job.heldForDiscovery,
  });
}

// ICT-8: the Smartsheet forms a decision is carried out with (mocked).
function formSteps(decision: ReturnType<typeof decide>): string[] {
  if (decision.action === 'POD') {
    return decision.outsideGuidelines
      ? ['Escalate to the ICT manager for POD approval, since it\'s outside the internal guideline']
      : ['Submit the Everything Else Form to start POD with the Fulfillment Center', 'Note "POD Full Sets <initials>" in MIC Special Instructions'];
  }
  if (decision.action === 'EMAIL_ONLY_CANCEL_FULLSETS') return ['Submit the Everything Else Form to release emails only and cancel full sets'];
  if (decision.action === 'ABORT') return ['Submit the Abort Jobs form', 'Highlight the job as aborted on the Red Report'];
  return [];
}

const FULFILLMENT_CENTER = { name: 'Fulfillment Center', email: 'proxymail@betanxt.com', source: 'contact_list' as const };

function buildPanel({ task, job, edits, groupJobs }: ProxyPanelContext): ProxyReviewPanelSchema {
  if (task.jobIds && task.jobIds.length > 1) return buildGroupPanel(groupJobs?.length ? groupJobs : [job]);
  const decision = decisionFor(job, edits);
  // ICT-1: a VIF/BRE Only mailing needs just the proxy card PDF — checked
  // before the filing search, since there's no proxy statement to pull.
  if (job.vifBreOnly && !['NO_ACTION', 'ABORT', 'HOLD_MAIL_DATE'].includes(decision.action)) return buildVifBrePanel(job, edits);
  if (decision.action === 'PULL_FILING') return buildPullFilingPanel(job, decision, filingField(job, edits));
  // ICT-1: a Yellow Broadridge job is checked on RICS instead of emailed.
  const ricsStatus = edits.ricsStatus ?? job.ricsStatus ?? 'Awaiting Material';
  const rics = job.isBroadridge && decision.action === 'FOLLOW_UP' && bandFor(daysToMeeting(job)) === 'Yellow'
    ? ricsAction(ricsStatus) : null;
  const contact = resolveOutreachContact(job);
  // ICT-1 Issuer follow-ups: last year's solicitor gets the normal country
  // template and is asked whether they're handling this year's job.
  const askLastYear = contact?.source === 'prior_year';
  const templateId = askLastYear ? (job.country === 'CA' ? 'FU-Canadian' : 'FU-American') : selectTemplate(job);
  const template = rics?.email === 'fulfillment'
    ? {
        subject: `Locate proxy material - ${job.issuer} (Job ${job.jobId})`,
        body: `Hello,\n\nBroadridge shows this job as Project Invoiced, so they consider all material mailed, but MIC doesn't show it received. Are you able to locate the material?\n\nJob #: ${job.jobId}\nCUSIP: ${job.cusip}\nIssuer: ${job.issuer}\n\nThank you,\nICT / Collections`,
      }
    : renderTemplate(templateId, job);
  let body = rics ? template.body : composeBody(decision, template.body);
  if (!rics && askLastYear) body = addComment(body, 'Are you handling the job this year?');
  if (!rics && job.followUpMailingMaterial) body = addComment(body, `This is a follow-up mailing. The material being mailed is the ${job.followUpMailingMaterial}.`);
  const recipient = rics?.email === 'fulfillment' ? FULFILLMENT_CENTER : contact;
  const ccEmails = rics?.email === 'fulfillment' ? DEFAULT_CC : job.isMediant ? mediantCc(job) : DEFAULT_CC;
  const shouldEmail = (rics ? rics.email !== null : EMAILABLE_ACTIONS.has(decision.action)) && !!recipient;

  const extractedFields: ProxyExtractedField[] = [
    { key: 'issuer', label: 'Issuer', display: job.issuer },
    { key: 'jobId', label: 'Job #', display: job.jobId },
    { key: 'cusip', label: 'CUSIP', display: job.cusip },
    { key: 'solicitor', label: 'Solicitor', display: job.solicitor || 'Issuer (no solicitor on record)' },
    { key: 'country', label: 'Country', display: job.country },
    { key: 'materialStatus', label: 'Material status', display: job.materialStatus },
    { key: 'outstanding', label: 'Outstanding', display: job.outstandingMaterialTypes.join(', ') || 'None' },
  ];
  const filing = filingField(job, edits);
  if (filing) extractedFields.push(filing);
  if (rics) {
    extractedFields.push({
      key: 'ricsStatus', label: 'RICS status (Ace read)', display: ricsStatus,
      editable: { kind: 'enum', value: ricsStatus, options: RICS_STATUSES.map(st => ({ value: st, label: st })) },
    });
  }

  // Special flags — informational call-outs, never blocking (mirrors
  // SHORTAGE_FOLLOWUP's "escalate to manager" pattern for POD below).
  const flagRows: ProxyCalcRow[] = [];
  if (rics) flagRows.push({ label: 'RICS portal', value: `CUSIP ${job.cusip.slice(0, 6)} → ${job.ricsJobNumber ?? 'Broadridge job'} · ${ricsStatus}` });
  if (job.isMediant) flagRows.push({ label: 'Mediant', value: 'Copies brokersearch@ and the team named in the MIC ops note.' });
  if (askLastYear) flagRows.push({ label: 'Last year', value: `No solicitor on the Broker Search Card. ${contact!.name} handled last year's job, so Ace asks if they're handling this one.` });
  if (job.followUpMailingMaterial) flagRows.push({ label: 'Follow-up mailing', value: `MIC notes say the ${job.followUpMailingMaterial} is being mailed, so the chase names it instead of the Proxy Statement.` });
  if (job.creditHold) flagRows.push({ label: 'Credit hold', value: 'Fulfillment is paused until the credit associate lifts the hold. Check with them before any POD or mailing.' });
  if (job.rdqNotSent) flagRows.push({ label: 'No RDQ sent', value: 'New Follow Up is greyed out in MIC, so Ace adds New Material first.' });

  if (rics?.email === 'fulfillment') flagRows.push({ label: 'Manager', value: 'Escalate to the ICT manager if the material can\'t be found (abort or move material).', tone: 'amber' });
  if (EMAILABLE_ACTIONS.has(decision.action) && !recipient) flagRows.push({ label: 'Contact', value: 'No contact on file. Contact tracing is needed (see Exceptions), so no email was drafted.', tone: 'amber' });
  if (decision.action === 'FOLLOW_UP' && !job.filingOnPortal) flagRows.push({ label: 'EDGAR/SEDAR', value: `No filing found for CUSIP ${job.cusip}, so send a Red follow-up instead.` });

  const micBody = rics
    ? rics.micNote
    : `${ACTION_LABEL[decision.action]}: ${decision.reason}${shouldEmail ? '' : ' No outreach sent.'}`;

  return {
    extractedFields,
    calculation: {
      title: 'Cutoff decision',
      ruleText: decision.reason,
      rows: [
        { label: 'Days to meeting', value: `${daysToMeeting(job)}d` },
        { label: 'Days to vote cutoff', value: `${job.daysToVoteCutoff}d` },
        ...(shouldEmail ? [{ label: 'Template selected', value: rics?.email === 'fulfillment' ? 'Fulfillment Center' : templateId }] : []),
        ...flagRows,
      ],
      totalRow: rics
        ? { label: 'Recommended action', value: shouldEmail ? (rics.email === 'broadridge' ? 'Ask Broadridge why it\'s closed' : 'Ask the fulfillment center to locate it') : 'Document in MIC, no email needed', tone: shouldEmail ? 'positive' : 'muted' }
        : { label: 'Recommended action', value: ACTION_LABEL[decision.action], tone: shouldEmail ? 'positive' : 'muted' },
      deltaRow: (decision.action === 'POD' && decision.outsideGuidelines)
        ? { label: 'Internal POD guideline', value: `Outside guideline (${job.pageCount}pp/${job.fullSetHolders} holders), so no-cost POD needs manager approval. Not shared externally.`, tone: 'amber' }
        : decision.consultManager
          ? { label: 'Manager check', value: 'Consult the ICT manager before cancelling full sets (ICT-8, 10–8 days).', tone: 'amber' }
          : undefined,
    },
    drafts: shouldEmail && recipient ? [
      {
        key: 'followup', icon: '📧', label: `${rics?.email === 'fulfillment' ? 'Locate material' : 'Solicitor follow-up'} → ${recipient.name}`,
        to: recipient.email, cc: ccEmails,
        note: contactNote(recipient),
        subject: template.subject, body,
      },
    ] : [],
    approveSummary: shouldEmail ? [
      'Send the solicitor follow-up email',
      ...formSteps(decision),
      ...(job.rdqNotSent ? [newMaterialStep(job)] : []),
      'Log a MIC follow-up documenting the decision',
      'Move this task to Done.',
    ] : [
      ...formSteps(decision),
      ...(job.rdqNotSent ? [newMaterialStep(job)] : []),
      rics ? `Log a MIC follow-up: "${rics.micNote}"` : `Log a MIC follow-up documenting the decision (${ACTION_LABEL[decision.action]}, no outreach)`,
      'Move this task to Done.',
    ],
    approveConfirmation: shouldEmail && recipient
      ? `Follow-up sent to <strong>${recipient.name}</strong> and documented for Job ${job.jobId}.`
      : `Decision documented for Job ${job.jobId}: ${ACTION_LABEL[decision.action]}, no outreach sent.`,
    closesTicket: decision.action === 'ABORT',
    escalation: decision.action === 'POD' && decision.outsideGuidelines
      ? `POD is outside the internal guideline (${job.pageCount} pages, ${job.fullSetHolders} full-set holders), so the manager decides (ICT-8).`
      : decision.consultManager ? 'Between 10 and 8 days to cutoff the manager decides whether to cancel the full sets (ICT-8).' : undefined,
    outboundActions: [
      ...(shouldEmail && recipient ? [{ key: 'followup', followUpType: templateId, recipientEmail: recipient.email, subject: template.subject, bodyPreview: body, ccEmails }] : []),
      { key: 'mic', followUpType: 'material_followup_documented', recipientEmail: 'mic@betanxt.com', subject: `Material follow-up documented for Job ${job.jobId}`, bodyPreview: micBody },
    ],
  };
}

function buildPullFilingPanel(job: ProxyJob, decision: ReturnType<typeof decide>, filing: ProxyExtractedField | null): ProxyReviewPanelSchema {
  const { portal, docType } = filingSource(job);
  const subject = `${docType} - ${job.issuer} (Job ${job.jobId}) · ${job.pageCount} pages`;
  const body = `Pulled from ${portal} by CUSIP ${job.cusip}.\n\n${docType}, ${job.pageCount} pages, attached. Please clear the job and release emails only. Full sets are still outstanding and may yet arrive; the POD decision is at 10 days to vote cutoff.`;
  const micBody = `${ACTION_LABEL[decision.action]}: ${decision.reason} ${docType} (${job.pageCount}pp) pulled from ${portal} and sent to ${VERIFICATIONS_MAILBOX} for email-only release.`;

  return {
    extractedFields: [
      { key: 'issuer', label: 'Issuer', display: job.issuer },
      { key: 'jobId', label: 'Job #', display: job.jobId },
      { key: 'cusip', label: 'CUSIP', display: job.cusip },
      { key: 'solicitor', label: 'Solicitor', display: job.solicitor || 'Issuer (no solicitor on record)' },
      { key: 'country', label: 'Country', display: job.country },
      { key: 'outstanding', label: 'Outstanding', display: job.outstandingMaterialTypes.join(', ') || 'None' },
      ...(filing ? [filing] : []),
    ],
    calculation: {
      title: 'Cutoff decision',
      ruleText: decision.reason,
      rows: [
        { label: 'Days to meeting', value: `${daysToMeeting(job)}d` },
        { label: 'Days to vote cutoff', value: `${job.daysToVoteCutoff}d` },
        { label: 'Portal', value: `${portal}, looked up by CUSIP ${job.cusip}` },
        { label: 'Filing found', value: `${docType} · ${job.pageCount} pages` },
      ],
      totalRow: { label: 'Recommended action', value: ACTION_LABEL[decision.action], tone: 'positive' },
    },
    drafts: [
      {
        key: 'filing', icon: '📄', label: `Filing hand-off → ${VERIFICATIONS_MAILBOX}`,
        to: VERIFICATIONS_MAILBOX, cc: DEFAULT_CC,
        subject, body,
      },
    ],
    approveSummary: [
      `Send the ${docType} to ${VERIFICATIONS_MAILBOX} to clear the job`,
      'Submit the Everything Else Form to release emails only',
      'Log a MIC follow-up (the job stays open while full sets are still outstanding)',
      'Move this task to Done.',
    ],
    approveConfirmation: `${docType} for Job ${job.jobId} pulled from ${portal} and sent to <strong>${VERIFICATIONS_MAILBOX}</strong> for email-only release.`,
    outboundActions: [
      { key: 'filing', followUpType: 'filing_pulled', recipientEmail: VERIFICATIONS_MAILBOX, subject, bodyPreview: body, ccEmails: DEFAULT_CC },
      { key: 'mic', followUpType: 'material_followup_documented', recipientEmail: 'mic@betanxt.com', subject: `Material follow-up documented for Job ${job.jobId}`, bodyPreview: micBody },
    ],
  };
}

// ICT-1: one email to the solicitor with every job in their section, and a
// Meeting Reminder follow-up logged in MIC on each job.
function buildGroupPanel(jobs: ProxyJob[]): ProxyReviewPanelSchema {
  const first = jobs[0];
  const contact = resolveOutreachContact(first)!;
  const templateId = gridTemplate(first);
  const { subject, body } = renderGridTemplate(templateId, first.solicitor!, jobs);
  const ccEmails = first.isMediant ? mediantCc(first) : DEFAULT_CC;
  const jobIds = jobs.map(j => j.jobId);
  const rows: ProxyCalcRow[] = jobs.map(j => {
    const notes = [
      j.followUpMailingMaterial && `follow-up mailing (${j.followUpMailingMaterial})`,
      j.rdqNotSent && 'no RDQ sent, New Material added first',
      j.creditHold && 'on credit hold',
    ].filter(Boolean);
    return { label: `${j.jobId} ${j.issuer}`, value: `${daysToMeeting(j)}d to meeting, ${j.fullSetsNeeded} full sets${j.country === 'CA' ? `, ${j.naaNeeded} NAA` : ''}${notes.length ? `. ${notes.join(', ')}` : ''}` };
  });
  return {
    extractedFields: [
      { key: 'solicitor', label: 'Solicitor', display: first.solicitor! },
      { key: 'contact', label: 'Contact', display: contact.email },
      { key: 'band', label: 'Report', display: `${bandFor(daysToMeeting(first))}, ${jobs.length} jobs` },
      { key: 'template', label: 'Template', display: templateId },
    ],
    calculation: {
      title: 'Follow-up grid',
      ruleText: 'ICT-1: work the report one solicitor at a time and send them one email listing every job in their section.',
      rows,
      totalRow: { label: 'Recommended action', value: `One ${templateId} email covering ${jobs.length} jobs`, tone: 'positive' },
    },
    drafts: [{ key: 'followup', icon: '📧', label: `Solicitor follow-up → ${contact.name}`, to: contact.email, cc: ccEmails, subject, body }],
    approveSummary: [
      `Send one email to ${contact.name} covering ${jobs.length} jobs`,
      ...jobs.filter(j => j.rdqNotSent).map(newMaterialStep),
      'Log a Meeting Reminder follow-up in MIC on each job',
      'Move this task to Done.',
    ],
    approveConfirmation: `Follow-up sent to <strong>${contact.name}</strong> for ${jobs.length} jobs and documented on each.`,
    outboundActions: [
      { key: 'followup', followUpType: templateId, recipientEmail: contact.email, ccEmails, subject, bodyPreview: body, jobIds },
      { key: 'mic', followUpType: 'material_followup_documented', recipientEmail: 'mic@betanxt.com', subject: `Material follow-up documented, ${jobs.length} jobs`, bodyPreview: `Follow up to ${first.solicitor}.`, jobIds },
    ],
  };
}

// ICT-1 VIF/BRE Only: the proxy card is the only material. Already in Job
// Documents → no chase, just the "VIF/BRE only" MIC note (which keeps the job
// off the report for 4 days). Not on file → ask the solicitor for the PDF;
// when it arrives, Material PDF routing sends it to documents@/verifications@.
function buildVifBrePanel(job: ProxyJob, edits: Record<string, any>): ProxyReviewPanelSchema {
  const onFile: boolean = edits.proxyCardOnFile ?? !!job.proxyCardPdfOnFile;
  const recipient = onFile ? null : resolveOutreachContact(job);
  const subject = `Proxy card PDF request - ${job.issuer} (Job ${job.jobId})`;
  const body = `Hello,\n\nThis job is mailing VIF/BRE only. Please send us a PDF copy of the proxy card.\n\nJob #: ${job.jobId}\nIssuer: ${job.issuer}\nMeeting Date: ${job.meetingDate}\n\nThank you,\nICT / Collections`;
  const ccEmails = job.isMediant ? mediantCc(job) : DEFAULT_CC;

  return {
    extractedFields: [
      { key: 'issuer', label: 'Issuer', display: job.issuer },
      { key: 'jobId', label: 'Job #', display: job.jobId },
      { key: 'solicitor', label: 'Solicitor', display: job.solicitor || 'Issuer (no solicitor on record)' },
      { key: 'special', label: 'Special Instructions', display: 'VIF/BRE Only' },
      {
        key: 'proxyCardOnFile', label: 'Proxy card PDF in Job Documents', display: onFile ? 'Yes' : 'No',
        editable: { kind: 'enum', value: onFile, options: [{ value: true, label: 'Yes' }, { value: false, label: 'No' }] },
      },
    ],
    calculation: {
      title: 'VIF/BRE Only',
      ruleText: 'ICT-1: a VIF/BRE Only mailing needs only the proxy card PDF.',
      rows: [
        { label: 'Days to meeting', value: `${daysToMeeting(job)}d` },
        { label: 'Days to vote cutoff', value: `${job.daysToVoteCutoff}d` },
        ...(!onFile && !recipient ? [{ label: 'Contact', value: 'No contact on file. Contact tracing is needed (see Exceptions), so no email was drafted.', tone: 'amber' as const }] : []),
      ],
      totalRow: onFile
        ? { label: 'Recommended action', value: 'No follow-up, just note "VIF/BRE only" in MIC', tone: 'muted' }
        : { label: 'Recommended action', value: 'Ask for the proxy card PDF', tone: 'positive' },
    },
    drafts: recipient ? [{ key: 'followup', icon: '📧', label: `Proxy card request → ${recipient.name}`, to: recipient.email, cc: ccEmails, note: contactNote(recipient), subject, body }] : [],
    approveSummary: onFile
      ? ['Log a MIC follow-up: "VIF/BRE only" (keeps the job off the report for 4 days)', 'Move this task to Done.']
      : [
          ...(recipient ? ['Ask the solicitor for a PDF copy of the proxy card'] : []),
          'Log a MIC follow-up documenting the request',
          'When the PDF arrives, it is routed to documents@ and verifications@',
          'Move this task to Done.',
        ],
    closesTicket: onFile,
    approveConfirmation: onFile
      ? `"VIF/BRE only" documented for Job ${job.jobId}. No follow-up needed.`
      : recipient
        ? `Proxy card PDF requested from <strong>${recipient.name}</strong> for Job ${job.jobId}.`
        : `Decision documented for Job ${job.jobId}. There's no contact on file.`,
    outboundActions: onFile
      ? [{ key: 'mic', followUpType: 'vif_bre_only', recipientEmail: 'mic@betanxt.com', subject: `VIF/BRE only for Job ${job.jobId}`, bodyPreview: 'VIF/BRE only. Proxy card PDF is already in Job Documents, so no follow-up is needed.' }]
      : [
          ...(recipient ? [{ key: 'followup', followUpType: selectTemplate(job), recipientEmail: recipient.email, subject, bodyPreview: body, ccEmails }] : []),
          { key: 'mic', followUpType: 'material_followup_documented', recipientEmail: 'mic@betanxt.com', subject: `Material follow-up documented for Job ${job.jobId}`, bodyPreview: 'VIF/BRE only. Requested a PDF copy of the proxy card.' },
        ],
  };
}

export const MATERIAL_FOLLOWUP_WORKFLOW: ProxyWorkflowDefinition = {
  type: 'MATERIAL_FOLLOWUP',
  label: 'Material Follow-Up',
  noun: 'a material follow-up',
  trigger: 'A human clicks "Create follow-up" on a job row in an Outstanding Material report (Red/Yellow/Green). Reports are static snapshots and never spawn tasks on their own.',
  source: 'Job signal: ProxyJob.materialFollowupRequestedAt, set only via the Reports page.',
  steps: ['Source & extraction', 'Cutoff decision', 'Draft & approve'],
  buildTasks,
  defaultEdits,
  buildPanel,
};
